import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { theme } from '../../styles/theme';
import { FaSoundcloud, FaSpotify, FaYoutube, FaFacebook } from 'react-icons/fa';

const ContactSection = styled.section`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${theme.spacing.lg} 0;
  background: ${theme.colors.primary};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" opacity="0.05"%3E%3Cpath d="M50,10 A40,40 0 0,1 90,50 A40,40 0 0,1 50,90 A40,40 0 0,1 10,50 A40,40 0 0,1 50,10 Z" fill="none" stroke="%238b0000" stroke-width="2"/%3E%3C/svg%3E')
      repeat;
    z-index: 0;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md} 0;
  }
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2.5rem, 5vw, 3rem);
  margin-bottom: ${theme.spacing.md};
  color: ${theme.colors.textLight};
  font-family: ${theme.fonts.heading};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-shadow: 0 0 10px rgba(139, 0, 0, 0.5);
`;

const CTAText = styled(motion.p)`
  text-align: center;
  font-size: clamp(1rem, 2vw, 1.2rem);
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.lg};
  flex-wrap: wrap;
  z-index: 1;

  @media (max-width: ${theme.breakpoints.sm}) {
    gap: ${theme.spacing.md};
  }
`;

const SocialLink = styled(motion.a)`
  color: ${theme.colors.accent};
  font-size: 2rem;
  background: ${theme.colors.glass.card};
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all ${theme.transitions.default};
  border: 1px solid transparent;

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }

  &:hover {
    color: ${theme.colors.textLight};
    background: ${theme.colors.accent};
    transform: translateY(-5px);
    border-color: ${theme.colors.textLight};
    box-shadow: 0 0 15px rgba(139, 0, 0, 0.7);
  }

  &:focus {
    outline: none;
    transform: scale(1.1);
    box-shadow: 0 0 15px rgba(139, 0, 0, 0.7);
  }

  /* Tooltip */
  &[data-tooltip]:hover:after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: ${theme.colors.glass.background};
    color: ${theme.colors.textLight};
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    border-radius: 4px;
    font-size: 0.9rem;
    white-space: nowrap;
    z-index: 2;
  }
`;

export default function Contact() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <ContactSection id="contact" role="region" aria-label="Contact Information">
      <motion.div
        className="container"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        style={{ maxWidth: '1200px', margin: '0 auto', width: '95%' }}
      >
        <SectionTitle variants={itemVariants} role="heading" aria-level={2}>
          Connect
        </SectionTitle>
        <CTAText variants={itemVariants} role="paragraph">
          Join the ritual. Follow S A B L E O R C H I D to experience the sound of silence, rebirth, and rage.
        </CTAText>
        <SocialLinks variants={containerVariants} role="list" aria-label="Social media links">
          <SocialLink
            href="https://soundcloud.com/sableorchid"
            target="_blank"
            rel="noopener noreferrer"
            variants={itemVariants}
            role="listitem"
            aria-label="Follow S A B L E O R C H I D on SoundCloud"
            data-tooltip="SoundCloud"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSoundcloud />
          </SocialLink>
          <SocialLink
            href="https://open.spotify.com/artist/sableorchid"
            target="_blank"
            rel="noopener noreferrer"
            variants={itemVariants}
            role="listitem"
            aria-label="Listen to S A B L E O R C H I D on Spotify"
            data-tooltip="Spotify"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSpotify />
          </SocialLink>
          <SocialLink
            href="https://youtube.com/@sableorchid"
            target="_blank"
            rel="noopener noreferrer"
            variants={itemVariants}
            role="listitem"
            aria-label="Watch S A B L E O R C H I D on YouTube"
            data-tooltip="YouTube"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaYoutube />
          </SocialLink>
          <SocialLink
            href="https://facebook.com/sableorchid"
            target="_blank"
            rel="noopener noreferrer"
            variants={itemVariants}
            role="listitem"
            aria-label="Follow S A B L E O R C H I D on Facebook"
            data-tooltip="Facebook"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaFacebook />
          </SocialLink>
        </SocialLinks>
      </motion.div>
    </ContactSection>
  );
}